<template>
  <div class="kivo-webhook-config">
    <div class="form-group">
      <label class="form-label">Webhook URL</label>
      <input
        v-model="webhookConfig.url"
        type="url"
        class="form-input"
        :class="{ 'input-error': showUrlError }"
        placeholder="https://connect.kivo.ai/hooks/..."
        @input="onFieldInput"
        @blur="touchValidation"
      />
      <span v-if="showUrlError" class="error-message">
        Webhook URL is required
      </span>
    </div>

    <div class="form-group">
      <div class="flex items-center justify-between mb-1">
        <label class="form-label">Notification text</label>
        <button
          type="button"
          class="insert-variable-btn"
          @click="showVariableDropdown = !showVariableDropdown"
        >
          Insert variable
        </button>
      </div>
      <div class="relative">
        <textarea
          ref="textTextarea"
          v-model="webhookConfig.text"
          class="form-textarea"
          :class="{ 'input-error': showTextError }"
          rows="4"
          placeholder='e.g. New lead: {{contact.name}} ({{contact.email}})'
          @input="onFieldInput"
          @blur="touchValidation"
        />
        <div v-if="showVariableDropdown" class="variable-dropdown">
          <div
            v-for="variable in availableVariables"
            :key="variable.key"
            class="variable-item"
            @click="insertVariable(variable.key)"
          >
            <span class="variable-label">{{ variable.label }}</span>
            <span class="variable-key">({{ variable.key }})</span>
          </div>
        </div>
      </div>
      <span v-if="showTextError" class="error-message">
        Notification text is required
      </span>
      <small class="form-help">
        Use variable placeholders like contact.name. The message is sent as JSON
        with a single text field when automation conditions match.
      </small>
    </div>
  </div>
</template>

<script>
import { MESSAGE_VARIABLES } from 'shared/constants/messages';

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    validation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      webhookConfig: {
        url: '',
        text: '',
      },
      showVariableDropdown: false,
      availableVariables: MESSAGE_VARIABLES,
    };
  },
  computed: {
    showValidationErrors() {
      return (
        this.validation?.action_params?.$dirty &&
        this.validation?.action_params?.$error
      );
    },
    showUrlError() {
      return this.showValidationErrors && !this.webhookConfig.url.trim();
    },
    showTextError() {
      return this.showValidationErrors && !this.webhookConfig.text.trim();
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (newValue && typeof newValue === 'object' && !Array.isArray(newValue)) {
          this.webhookConfig = {
            url: newValue.url || '',
            text: newValue.text || '',
          };
        }
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    updateValue() {
      this.$emit('input', {
        url: this.webhookConfig.url.trim(),
        text: this.webhookConfig.text,
      });
    },
    onFieldInput() {
      this.updateValue();
      this.touchValidation();
    },
    touchValidation() {
      this.validation?.action_params?.$touch();
    },
    insertVariable(variableKey) {
      const textarea = this.$refs.textTextarea;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = this.webhookConfig.text;
      const variableText = `{{${variableKey}}}`;

      this.webhookConfig.text =
        text.substring(0, start) + variableText + text.substring(end);

      this.$nextTick(() => {
        const newPosition = start + variableText.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
        this.onFieldInput();
      });

      this.showVariableDropdown = false;
    },
    handleClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showVariableDropdown = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.kivo-webhook-config {
  @apply mt-2 space-y-3;
}

.form-group {
  @apply flex flex-col;
}

.form-label {
  @apply text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.form-input,
.form-textarea {
  @apply w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-md
         bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
         focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent;
  min-height: 40px;
}

.form-textarea {
  @apply font-mono text-xs;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.form-help {
  @apply text-xs text-slate-500 dark:text-slate-400 mt-1;
}

.input-error {
  @apply border-red-500 focus:ring-red-500;
}

.error-message {
  @apply text-xs text-red-600 dark:text-red-400 mt-1;
}

.insert-variable-btn {
  @apply px-3 py-1 text-xs font-medium text-woot-600 dark:text-woot-400
         bg-woot-50 dark:bg-woot-900 border border-woot-200 dark:border-woot-700
         rounded-md hover:bg-woot-100 dark:hover:bg-woot-800
         transition-colors duration-200;
}

.variable-dropdown {
  @apply absolute z-50 mt-1 w-full overflow-y-auto
         bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600
         rounded-md shadow-lg;
  top: 100%;
  left: 0;
  max-height: 12.5rem; /* ~5 visible items */
}

.variable-item {
  @apply px-3 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700
         flex items-center justify-between border-b border-slate-100 dark:border-slate-700
         last:border-b-0;
  min-height: 2.5rem;

  .variable-label {
    @apply text-sm font-medium text-slate-700 dark:text-slate-200;
  }

  .variable-key {
    @apply text-xs text-slate-500 dark:text-slate-400 font-mono;
  }
}
</style>
